import { Card, SimpleGrid, Stat, Text } from '@chakra-ui/react'
import type { ReactNode } from 'react'

/**
 * The four service types, with the label and the unit they are counted in.
 * Units are spelled out; the layout truncates them when a column gets narrow.
 */
const SERVICES = [
  { key: 'TOJ', label: 'TOJ', unit: 'Stück' },
  { key: 'CONSULTING', label: 'Beratung', unit: 'Stunden' },
  { key: 'TRAINING', label: 'Schulung', unit: 'Tage' },
  { key: 'PILOT', label: 'Pilot', unit: 'Einheiten' },
] as const

export type WorkloadServiceKey = (typeof SERVICES)[number]['key']

type WorkloadQuotaCardProps = {
  title: string
  /** Value per service type. A missing entry renders as zero. */
  values: Partial<Record<WorkloadServiceKey, number>>
  /** Replaces the figures, for example while loading or when nothing is selected. */
  placeholder?: ReactNode
}

/**
 * The quota figures for the four service types, shown wherever a remaining
 * workload is displayed. One implementation, so the labels, units, and layout
 * cannot drift apart between the pages that show it.
 */
export function WorkloadQuotaCard({ title, values, placeholder }: WorkloadQuotaCardProps) {
  return (
    <Card.Root>
      <Card.Body>
        <Card.Title>{title}</Card.Title>

        {placeholder ?? (
          <SimpleGrid columns={{ base: 2, md: 4 }} gap="stack">
            {SERVICES.map(service => (
              <Stat.Root key={service.key} minW={0}>
                <Stat.Label truncate>{service.label}</Stat.Label>
                <Stat.ValueText>{values[service.key] ?? 0}</Stat.ValueText>
                <Stat.HelpText truncate>{service.unit}</Stat.HelpText>
              </Stat.Root>
            ))}
          </SimpleGrid>
        )}
      </Card.Body>
    </Card.Root>
  )
}

/** Renders explanatory copy in place of the figures. */
export function WorkloadQuotaHint({ children }: { children: ReactNode }) {
  return <Text color="fg.muted">{children}</Text>
}
